import React from 'react'
import { COLORS } from '../styles/tokens'
import { ss } from './gameSetupStyles'

export interface GameCompleteStat {
  value: string
  label: string
}

export interface GameCompleteScreenProps {
  emoji:            string
  title:            string
  subtitle:         string
  stats:            GameCompleteStat[]
  playAgainLabel:   string
  changeLevelLabel: string
  backLabel:        string
  onPlayAgain:      () => void
  onChangeLevel:    () => void
  onBack:           () => void
}

export default function GameCompleteScreen({
  emoji, title, subtitle, stats,
  playAgainLabel, changeLevelLabel, backLabel,
  onPlayAgain, onChangeLevel, onBack,
}: GameCompleteScreenProps) {
  const useGrid = stats.length >= 4
  return (
    <div style={s.root}>
      <div style={s.bg1} aria-hidden />
      <div style={s.bg2} aria-hidden />
      <div style={s.card} className="fade-up">
        <div style={s.emoji}>{emoji}</div>
        <h1 style={s.title}>{title}</h1>
        <p style={s.subtitle}>{subtitle}</p>

        <div style={useGrid ? s.statsGrid : s.statsRow}>
          {stats.map((stat, i) => (
            <React.Fragment key={i}>
              {i > 0 && !useGrid && <div style={s.divider} />}
              <div style={s.stat}>
                <span style={s.statValue}>{stat.value}</span>
                <span style={s.statLabel}>{stat.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div style={s.buttons}>
          <button onClick={onPlayAgain}   style={ss.btnPrimary}>  {playAgainLabel}   </button>
          <button onClick={onChangeLevel} style={ss.btnSecondary}>{changeLevelLabel}</button>
          <button onClick={onBack}        style={ss.btnGhost}>    {backLabel}        </button>
        </div>
      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:      '100vh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     `linear-gradient(160deg, ${COLORS.greenDark} 0%, ${COLORS.green} 50%, #3D7A5F 100%)`,
    padding:        '32px 20px',
    position:       'relative',
    overflow:       'hidden',
  },
  bg1: {
    position:      'absolute' as const,
    top:           -100,
    right:         -100,
    width:         400,
    height:        400,
    borderRadius:  '50%',
    background:    'rgba(255,255,255,0.04)',
    pointerEvents: 'none',
  },
  bg2: {
    position:      'absolute' as const,
    bottom:        -80,
    left:          -80,
    width:         320,
    height:        320,
    borderRadius:  '50%',
    background:    'rgba(212,160,23,0.07)',
    pointerEvents: 'none',
  },
  card: {
    position:      'relative',
    zIndex:        1,
    background:    '#FFFFFF',
    borderRadius:  24,
    padding:       '48px 40px',
    maxWidth:      440,
    width:         '100%',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           20,
    boxShadow:     '0 24px 80px rgba(0,0,0,0.25)',
    textAlign:     'center',
  },
  emoji: {
    fontSize:   '3.5rem',
    lineHeight: 1,
    animation:  'floatLeaf 3s ease-in-out infinite',
  },
  title: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   '2.8rem',
    fontWeight: 700,
    color:      COLORS.greenDark,
    lineHeight: 1.05,
    margin:     0,
  },
  subtitle: {
    fontSize:   '1.05rem',
    color:      '#6B5C50',
    fontWeight: 500,
    margin:     0,
  },
  statsRow: {
    display:        'flex',
    alignItems:     'center',
    gap:            20,
    background:     COLORS.bgPage,
    borderRadius:   14,
    padding:        '18px 24px',
    width:          '100%',
    justifyContent: 'center',
    border:         `1.5px solid ${COLORS.borderWarm}`,
  },
  statsGrid: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:                 '16px 20px',
    background:          COLORS.bgPage,
    borderRadius:        14,
    padding:             '18px 24px',
    width:               '100%',
    border:              `1.5px solid ${COLORS.borderWarm}`,
  },
  stat: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           4,
  },
  statValue: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   '2rem',
    fontWeight: 700,
    color:      COLORS.greenDark,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: '0.875rem',
    fontWeight:    700,
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color:         '#9A8070',
  },
  divider: {
    width:      1.5,
    height:     36,
    background: '#E3D5C0',
  },
  buttons: {
    display:       'flex',
    flexDirection: 'column',
    gap:           10,
    width:         '100%',
    marginTop:     4,
  },
}
